/**
 * buildItemsSnapshot — frontend helper mirroring the backend
 * `services/catalog_snapshot.build_items_snapshot`.
 *
 * Freezes a program step's `defaultItems: [{itemId, quantity?}]` into an
 * immutable `items[]` snapshot rendered on generated visits. Uses a
 * pre-loaded catalog map (from useCatalogItems.byId) so no additional
 * Firestore reads happen at seed / booking time.
 *
 * Missing items are still preserved with `nameAtSnapshot="(unknown item)"`
 * so historical references never silently vanish.
 *
 * @param {Object} catalogById  — { [itemId]: catalogItem }
 * @param {Array}  defaultItems — raw programStep.defaultItems
 * @param {string} source       — "program_default" | "visit_override"
 * @returns {Array} snapshot rows
 */
export function buildItemsSnapshot(catalogById, defaultItems, source = 'program_default') {
  if (!Array.isArray(defaultItems) || defaultItems.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const entry of defaultItems) {
    if (!entry || typeof entry !== 'object') continue;
    const itemId = entry.itemId;
    if (!itemId || typeof itemId !== 'string' || seen.has(itemId)) continue;
    seen.add(itemId);
    const qty = toNumberOrNull(entry.quantity);
    const item = (catalogById || {})[itemId];
    if (!item) {
      out.push({
        itemId,
        nameAtSnapshot: '(unknown item)',
        sku: null,
        unit: null,
        quantity: qty,
        source,
      });
      continue;
    }
    out.push({
      itemId,
      nameAtSnapshot: item.name || '(unnamed item)',
      sku: item.sku || null,
      unit: item.unit || null,
      quantity: qty,
      source,
    });
  }
  return out;
}

function toNumberOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
